import { useQuery } from '@apollo/client'
import styled from '@emotion/styled'
import {
  Business as BusinessIcon,
  Category as CategoryIcon,
  ExpandMore as ExpandMoreIcon,
  ImageNotSupported as ImageNotSupportedIcon,
  Inventory2 as Inventory2Icon,
  Loyalty as LoyaltyIcon,
  QrCode as QrCodeIcon,
} from '@mui/icons-material'
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { useState } from 'react'

import {
  GET_POS_PRODUCT_INFO,
  GET_SIMPLE_PRODUCT,
  GetPosProductInfoQuery,
  GetPosProductInfoQueryVariables,
  GetSimpleProductQuery,
  GetSimpleProductQueryVariables,
} from '@/graphql'
import useT from '@/i18n/useT'
import { ProductHelpers } from '@/utils/products.helpers'

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: theme.shadows[2],
  borderRadius: theme.shape.borderRadius * 2,
  [theme.breakpoints.up('sm')]: {
    minWidth: 500,
  },
}))

const ImagePlaceholder = ({ alt }: { alt: string }) => {
  return (
    <Box
      sx={{
        width: '100%',
        height: 240,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        bgcolor: 'action.hover',
        color: 'text.disabled',
        borderRadius: 2,
      }}
    >
      <ImageNotSupportedIcon sx={{ fontSize: 48, mb: 1 }} />
      <Typography variant="caption">{alt}</Typography>
    </Box>
  )
}

export type ProductInitialInfo = {
  preferredName?: string | null
  name?: string | null
  packagingName?: string | null
  planogramImageUrl?: string | null
  marketingImageUrl?: string | null
}

const ProductDetailsCard = ({
  upc,
  hideDetails = false,
  hideBrandTag = true,
  hideCategoryTag = true,
  smallProductName = false,
  hideAlsoKnownAs = false,
  fallbackName,
  preferredNameOverride,
  initialInfo,
}: {
  upc: string
  hideDetails?: boolean
  hideBrandTag?: boolean
  hideCategoryTag?: boolean
  smallProductName?: boolean
  hideAlsoKnownAs?: boolean
  fallbackName?: string
  preferredNameOverride?: string | null
  initialInfo?: ProductInitialInfo
}) => {
  const { t } = useT()
  const [expanded, setExpanded] = useState(false)

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const { data, loading } = useQuery<
    GetSimpleProductQuery,
    GetSimpleProductQueryVariables
  >(GET_SIMPLE_PRODUCT, {
    variables: { upc },
    skip: !!(hideDetails && initialInfo),
  })

  const { data: posData, loading: posLoading } = useQuery<
    GetPosProductInfoQuery,
    GetPosProductInfoQueryVariables
  >(GET_POS_PRODUCT_INFO, {
    variables: { upc },
    skip: !!(data?.product ?? initialInfo) || !!fallbackName,
  })

  const displayName = fallbackName || posData?.posProductInfo?.description

  const isMissingFromCatalog = !(data?.product ?? initialInfo)

  if ((loading && !initialInfo) || posLoading) {
    return (
      <StyledCard
        variant="outlined"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
          flex: 1,
        }}
      >
        <CircularProgress />
      </StyledCard>
    )
  }

  if (isMissingFromCatalog && !displayName && !preferredNameOverride) {
    return (
      <StyledCard
        variant="outlined"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          minHeight: 200,
          flex: 1,
          p: 4,
        }}
      >
        <Alert severity="warning" sx={{ width: '100%' }}>
          <Typography variant="h5" component="h2">
            {t('productDetails.missingFromCatalog')}
          </Typography>
          <Typography>{upc}</Typography>
        </Alert>
      </StyledCard>
    )
  }

  const resolvedPreferredName =
    preferredNameOverride ||
    ProductHelpers.getProductPreferredName(data?.product ?? initialInfo, upc) ||
    displayName

  const product = data?.product || {
    preferredName: resolvedPreferredName,
    upcA: upc,
    brand: { name: t('common.unknown') },
    category: {
      name: posData?.posProductInfo?.category || t('common.unknown'),
    },
    marketingImageUrl:
      initialInfo?.marketingImageUrl || initialInfo?.planogramImageUrl || null,
    commonName: null,
    packagingName: initialInfo?.packagingName || t('common.unknown'),
    packaging: { packagingString: t('common.unknown') },
    manufacturer: { name: t('common.unknown') },
  }

  return (
    <StyledCard variant="outlined">
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
        <Box
          sx={{
            width: { xs: '100%', md: '40%' },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2,
          }}
        >
          {product.marketingImageUrl ? (
            <CardMedia
              component="img"
              image={product.marketingImageUrl}
              alt={resolvedPreferredName || t('productDetails.imageAlt')}
              sx={{
                maxHeight: 100,
                objectFit: 'contain',
                borderRadius: 1,
              }}
            />
          ) : (
            <ImagePlaceholder
              alt={resolvedPreferredName || t('productDetails.imageAlt')}
            />
          )}
        </Box>
        <Box sx={{ width: { xs: '100%', md: '60%' } }}>
          <CardContent
            sx={{
              gap: 2,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack direction={'row'} flexWrap={'wrap'} gap={1}>
              {hideBrandTag ? null : (
                <Chip
                  avatar={
                    <Avatar>
                      <LoyaltyIcon fontSize="inherit" />
                    </Avatar>
                  }
                  label={
                    product.brand?.name || t('productDetails.chip.noBrand')
                  }
                  variant="outlined"
                />
              )}
              {hideCategoryTag ? null : (
                <Chip
                  avatar={
                    <Avatar>
                      <CategoryIcon fontSize="inherit" />
                    </Avatar>
                  }
                  label={
                    product.category?.name ||
                    t('productDetails.chip.noCategory')
                  }
                  variant="outlined"
                  color="default"
                />
              )}
            </Stack>
            <Box>
              <Typography
                variant={smallProductName ? 'body1' : 'h5'}
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                {resolvedPreferredName ||
                  t('productDetails.unknownProductName')}
              </Typography>
              {product.commonName?.name &&
                !hideAlsoKnownAs &&
                product.commonName?.name !== resolvedPreferredName && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t('productDetails.alsoKnownAs', {
                      name: product.commonName.name,
                    })}
                  </Typography>
                )}

              {!!product.packagingName && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('productDetails.label.packaging')}: {product.packagingName}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('productDetails.label.upc')}: {product.upcA}
              </Typography>
            </Box>
            <Box>
              {!hideDetails && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="button"
                    sx={{
                      fontSize: '0.75rem',
                      color: 'primary.main',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={handleExpandClick}
                  >
                    {expanded
                      ? t('productDetails.hideDetails')
                      : t('productDetails.showMoreDetails')}
                    <IconButton
                      size="small"
                      sx={{
                        ml: 0.5,
                        p: 0.5,
                        transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                      }}
                    >
                      <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                  </Typography>
                </Box>
              )}
              <Collapse in={expanded} timeout={600} unmountOnExit>
                <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    {t('productDetails.additionalInformation')}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LoyaltyIcon
                          sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                          >
                            {t('productDetails.label.brand')}:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ ml: 0.5 }}
                            component="span"
                          >
                            {product.brand?.name ||
                              t('productDetails.noBrandName')}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CategoryIcon
                          sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                          >
                            {t('productDetails.label.category')}:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ ml: 0.5 }}
                            component="span"
                          >
                            {product.category?.name ||
                              t('productDetails.noCategoryName')}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <QrCodeIcon
                          sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                          >
                            {t('productDetails.label.upc')}:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ ml: 0.5 }}
                            component="span"
                          >
                            {product.upcA}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Inventory2Icon
                          sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                          >
                            {t('productDetails.label.packaging')}:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ ml: 0.5 }}
                            component="span"
                          >
                            {product.packaging?.packagingString ||
                              t('productDetails.noPackaging')}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon
                          sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }}
                        />
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                          >
                            {t('productDetails.label.manufacturer')}:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ ml: 0.5 }}
                            component="span"
                          >
                            {product.manufacturer?.name ||
                              t('productDetails.noManufacturer')}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </Box>
          </CardContent>
        </Box>
      </Box>
    </StyledCard>
  )
}

export default ProductDetailsCard
